import React, { useState, useEffect } from 'react';
import {
  ExternalLink,
  Github,
  ChevronRight,
  Filter,
  Smartphone,
  Globe,
  Zap
} from 'lucide-react';
import ina from '../../assets/assets/ina.png';
import synacore from '../../assets/assets/synacore.png';
import attendo from '../../assets/assets/attendo.png';
import athletix from '../../assets/assets/athletix.png';
import hospital_managemene from '../../assets/assets/hospital_managemene.png';
import Student from '../../assets/assets/Student.png';
import Block_chain from '../../assets/assets/Block_chain.png';
import ml from '../../assets/assets/ml.png';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Responsive breakpoint detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force reset to 'all' on mount
  useEffect(() => {
    setActiveFilter('all');
  }, []);

  const filters = [
    { id: 'all', label: 'All Projects', icon: Filter },
    { id: 'web', label: 'Web Apps', icon: Globe },
    { id: 'mobile', label: 'Mobile', icon: Smartphone },
    { id: 'others', label: 'Others', icon: Zap },
  ];

  const projects = [
    {
      id: 1,
      title: 'ina creations',
      description: 'A dynamic portfolio website crafted for a media production startup, "ina creations." This is a robust React application deployed on Vercel, featuring a secure admin login built with Firebase for seamless content updates and real-time data synchronization.',
      image: ina,
      category: 'web',
      technologies: ['React.js', 'JavaScript (ES6+)', 'Firebase', 'Vercel', 'Responsive Design'],
      demoUrl: 'https://ina-creations.vercel.app',
      githubUrl: 'https://github.com/Nithinreddy000/ina_creations.git',
      featured: false
    },
    {
      id: 2,
      title: 'synacore tech',
      description: 'The official portfolio website for my personal agency, Synacore Tech. Developed as a high-performance React application, ensuring optimal load times and a fluid user interface.',
      image: synacore,
      category: 'web',
      technologies: ['React.js', 'JavaScript', 'Hostinger', 'SEO Optimization', 'Webpack'],
      demoUrl: 'https://synacoretech.com/',
      githubUrl: null,
      featured: false
    },
    {
      id: 3,
      title: 'attendo',
      description: 'An intelligent attendance management system developed for event leads to accurately track club member activity and engagement. Built as a sophisticated React application with real-time statistical dashboards.',
      image: attendo,
      category: 'web',
      technologies: ['React.js', 'JavaScript', 'Firebase', 'Vercel', 'Data Visualization'],
      demoUrl: 'https://attendo-topaz.vercel.app',
      githubUrl: 'https://github.com/Nithinreddy000/GDGCLUB_Attendance.git',
      featured: false
    },
    {
      id: 4,
      title: 'athletix',
      description: 'An all-in-one Athlete Management System designed specifically for the Indian sports landscape, integrating AI-powered motion analysis, 3D injury visualization, financial management, and team collaboration.',
      image: athletix,
      category: 'mobile',
      technologies: ['Flutter', 'Dart', 'Firebase', 'TensorFlow', 'Google Cloud'],
      demoUrl: 'https://athletemanagementsystem-8fa62.web.app/',
      githubUrl: 'https://github.com/Nithinreddy000/Athletix_app.git',
      featured: true
    },
    {
      id: 5,
      title: 'hospital management',
      description: 'A comprehensive hospital management system featuring a custom slot booking flow with 100% conflict handling across receptionists, doctors, and patients. Integrates WebRTC for secure in-app video calls.',
      image: hospital_managemene,
      category: 'mobile',
      technologies: ['Flutter', 'Dart', 'Firebase', 'WebRTC', 'Google Document AI'],
      demoUrl: null,
      githubUrl: null,
      featured: true
    },
    {
      id: 6,
      title: 'Student Management System',
      description: 'A robust Student Information System (SIS) designed to centralize student data and streamline administrative operations within educational institutions.',
      image: Student,
      category: 'web',
      technologies: ['MySQL', 'PHP', 'HTML5', 'CSS3', 'JavaScript', 'Bootstrap'],
      demoUrl: null,
      githubUrl: 'https://drive.google.com/file/d/1x75fMG3iGxYY9wvaqeOAlP4Xq9LUx9hl/view?usp=drive_link',
      featured: false
    },
    {
      id: 7,
      title: 'Health Insurance Fraud Detection',
      description: 'An advanced health insurance fraud detection system that innovatively combines machine learning with blockchain technology for secure, tamper-proof claim validation.',
      image: Block_chain,
      category: 'others',
      technologies: ['Python', 'Scikit-learn', 'Solidity', 'Ethereum', 'Power BI'],
      demoUrl: null,
      githubUrl: null,
      featured: false
    },
    {
      id: 8,
      title: 'Multilingual Offensive Language Detection',
      description: 'A robust multilingual offensive language detection system utilizing classical machine learning techniques with sophisticated preprocessing and feature extraction.',
      image: ml,
      category: 'others',
      technologies: ['Python', 'NLP', 'Naive Bayes', 'Logistic Regression', 'OpenAI Whisper'],
      demoUrl: null,
      githubUrl: null,
      featured: false
    },
  ];

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  const handleFilterClick = (filterId) => {
    setActiveFilter(filterId);
  };

  const ProjectCard = ({ project }) => {
    const isReportLink = project.githubUrl && project.githubUrl.includes('drive.google.com');
    
    return (
      <div
        style={{
          ...styles.projectCard,
          ...(project.featured && !isMobile ? styles.featuredCard : {}),
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 30px rgba(150, 199, 185, 0.25)';
            e.currentTarget.style.borderColor = 'rgba(150, 199, 185, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(150, 199, 185, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(150, 199, 185, 0.3)';
          }
        }}
      >
        {/* Project Image */}
        <div style={styles.imageContainer}>
          <img
            src={project.image}
            alt={project.title}
            style={{
              ...styles.projectImage,
              height: isMobile ? '200px' : project.featured ? '280px' : '220px'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.target.style.transform = 'scale(1)';
              }
            }}
          />
          
          {/* Quick Actions - Only on Desktop */}
          {!isMobile && (
            <div style={styles.quickActions} className="project-actions">
              {project.demoUrl && (
                <a
                  href={project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.quickActionButton}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  <ExternalLink size={16} />
                </a>
              )}
              {project.githubUrl && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.quickActionButtonSecondary}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  {isReportLink ? <ExternalLink size={16} /> : <Github size={16} />}
                </a>
              )}
            </div>
          )}
          
          {/* Featured Badge */}
          {project.featured && (
            <div style={styles.featuredBadge}>
              Featured
            </div>
          )}
        </div>

        {/* Project Info */}
        <div style={styles.projectInfo}>
          <div style={styles.projectHeader}>
            <h3 style={styles.projectTitle}>
              {project.title}
            </h3>
            <ChevronRight
              size={isMobile ? 18 : 20}
              style={styles.chevronIcon}
            />
          </div>
          
          <p style={styles.projectDescription}>
            {project.description}
          </p>
          
          {/* Technologies */}
          <div style={styles.technologiesContainer}>
            {project.technologies.slice(0, isMobile ? 4 : 6).map((tech) => (
              <span key={tech} style={styles.technologyTag}>
                {tech}
              </span>
            ))}
            {project.technologies.length > (isMobile ? 4 : 6) && (
              <span style={styles.technologyTag}>
                +{project.technologies.length - (isMobile ? 4 : 6)}
              </span>
            )}
          </div>
          
          {/* Actions */}
          <div style={styles.actionsContainer}>
            {project.demoUrl && (
              <a
                href={project.demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.actionButton}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.2)';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.1)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <ExternalLink size={16} />
                Live Demo
              </a>
            )}
            {project.githubUrl && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.actionButtonSecondary}
                onMouseEnter={(e) => {
                  e.target.style.color = '#2C3E50';
                  e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.3)';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#546C81';
                  e.target.style.backgroundColor = 'rgba(150, 199, 185, 0.2)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                {isReportLink ? <ExternalLink size={16} /> : <Github size={16} />}
                {isReportLink ? 'Report' : 'Code'}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="projects" style={styles.section}>
      <div style={styles.container}>
        {/* Section Header */}
        <div style={styles.header}>
          <p style={styles.subtitle}>
            My Work
          </p>
          <h2 style={styles.title}>
            Featured{' '}
            <span style={styles.titleGradient}>
              Projects
            </span>
          </h2>
          <p style={styles.description}>
            A showcase of my recent work across web development, mobile apps, and design projects
          </p>
        </div>

        {/* Filter Buttons */}
        <div style={styles.filterContainer}>
          {filters.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilter === filter.id;
            
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => handleFilterClick(filter.id)}
                style={{
                  ...styles.filterButton,
                  ...(isActive ? styles.activeFilterButton : {}),
                  padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                }}
              >
                <Icon size={isMobile ? 16 : 18} />
                {isMobile ? filter.label.split(' ')[0] : filter.label}
              </button>
            );
          })}
        </div>

        {/* Projects Grid */}
        <div style={{
          ...styles.projectsGrid,
          gridTemplateColumns: isMobile 
            ? '1fr' 
            : isTablet 
            ? 'repeat(auto-fit, minmax(300px, 1fr))'
            : 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: isMobile ? '1.5rem' : '2rem'
        }}>
          {filteredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>

        {/* More Projects CTA */}
        <div style={styles.ctaContainer}>
          <a
            href="https://github.com/Nithinreddy000"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...styles.ctaButton,
              padding: isMobile ? '0.875rem 1.5rem' : '1rem 2rem',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#96C7B9';
              e.target.style.color = '#FFFFFF';
              e.target.style.transform = 'scale(1.05) translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#96C7B9';
              e.target.style.transform = 'scale(1) translateY(0)';
            }}
          >
            <Github size={isMobile ? 18 : 20} />
            View More on GitHub
          </a>
        </div>
      </div>

      {/* CSS Overrides */}
      <style jsx>{`
        button {
          all: unset;
          box-sizing: border-box;
        }
        
        .project-actions {
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        div:hover .project-actions {
          opacity: 1;
        }
        
        @media (max-width: 768px) {
          .project-actions {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
};

const styles = {
  section: {
    padding: 'clamp(3rem, 8vw, 5rem) 0',
    backgroundColor: '#FFFFFF',
    position: 'relative'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 clamp(1rem, 4vw, 2rem)'
  },
  header: {
    textAlign: 'center',
    marginBottom: 'clamp(3rem, 6vw, 4rem)'
  },
  subtitle: {
    color: '#96C7B9',
    fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '1rem',
    margin: '0 0 1rem 0'
  },
  title: {
    fontFamily: 'Comfortaa, sans-serif',
    fontSize: 'clamp(2rem, 6vw, 3rem)',
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: '1rem',
    margin: '0 0 1rem 0'
  },
  titleGradient: {
    background: 'linear-gradient(135deg, #96C7B9 0%, #7BB5A7 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  description: {
    fontSize: 'clamp(1rem, 3vw, 1.2rem)',
    color: '#546C81',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6'
  },
  filterContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '1rem',
    marginBottom: 'clamp(2rem, 5vw, 3rem)'
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'transparent',
    color: '#546C81',
    border: '2px solid #96C7B9',
    borderRadius: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    outline: 'none',
    fontFamily: 'inherit',
    userSelect: 'none'
  },
  activeFilterButton: {
    backgroundColor: '#96C7B9',
    color: '#FFFFFF',
    borderColor: '#96C7B9'
  },
  projectsGrid: {
    display: 'grid',
    marginBottom: 'clamp(2rem, 5vw, 3rem)'
  },
  projectCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '2px solid rgba(150, 199, 185, 0.3)',
    boxShadow: '0 4px 15px rgba(150, 199, 185, 0.1)',
    transition: 'all 0.3s ease',
    position: 'relative',
    height: 'fit-content'
  },
  featuredCard: {
    gridColumn: 'span 2'
  },
  imageContainer: {
    position: 'relative',
    overflow: 'hidden'
  },
  projectImage: {
    width: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease'
  },
  quickActions: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    display: 'flex',
    gap: '0.5rem',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  quickActionButton: {
    padding: '0.5rem',
    backgroundColor: 'rgba(150, 199, 185, 0.9)',
    borderRadius: '50%',
    color: '#FFFFFF',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  quickActionButtonSecondary: {
    padding: '0.5rem',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '50%',
    color: '#2C3E50',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  featuredBadge: {
    position: 'absolute',
    top: '1rem',
    left: '1rem',
    padding: '0.25rem 0.75rem',
    backgroundColor: 'rgba(150, 199, 185, 0.9)',
    borderRadius: '20px',
    fontSize: 'clamp(0.7rem, 2vw, 0.75rem)',
    fontWeight: '600',
    color: '#FFFFFF'
  },
  projectInfo: {
    padding: 'clamp(1rem, 3vw, 1.5rem)'
  },
  projectHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem'
  },
  projectTitle: {
    fontFamily: 'Comfortaa, sans-serif',
    fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
    fontWeight: '600',
    color: '#2C3E50',
    margin: 0
  },
  chevronIcon: {
    color: '#546C81',
    transition: 'all 0.3s ease',
    flexShrink: 0
  },
  projectDescription: {
    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
    color: '#546C81',
    lineHeight: '1.6',
    marginBottom: '1rem',
    margin: '0 0 1rem 0'
  },
  technologiesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '1rem'
  },
  technologyTag: {
    padding: '0.25rem 0.5rem',
    backgroundColor: 'rgba(150, 199, 185, 0.2)',
    border: '1px solid rgba(150, 199, 185, 0.3)',
    borderRadius: '8px',
    fontSize: 'clamp(0.7rem, 2vw, 0.75rem)',
    color: '#546C81',
    fontWeight: '500'
  },
  actionsContainer: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: 'rgba(150, 199, 185, 0.1)',
    border: '1px solid rgba(150, 199, 185, 0.3)',
    borderRadius: '8px',
    color: '#96C7B9',
    textDecoration: 'none',
    fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    minHeight: '44px'
  },
  actionButtonSecondary: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: 'rgba(150, 199, 185, 0.2)',
    border: '1px solid rgba(150, 199, 185, 0.3)',
    borderRadius: '8px',
    color: '#546C81',
    textDecoration: 'none',
    fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    minHeight: '44px'
  },
  ctaContainer: {
    textAlign: 'center'
  },
  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'transparent',
    color: '#96C7B9',
    border: '2px solid #96C7B9',
    borderRadius: '12px',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    minHeight: '48px'
  }
};

export default Projects;
